import {
  useEffect,
  useState,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from "react";

const INTRO_TEXT = "MY NAME IS MASON, AND I'M";
const DESCRIPTORS = [
  "A SOFTWARE ENGINEER",
  "A VETERAN",
  "A PHOTOGRAPHER",
  "A SYSTEMS BUILDER",
  "A PROBLEM SOLVER",
] as const;
const BOARD_COLUMNS = INTRO_TEXT.length;
const FLAP_CHARACTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789&'.,-";
const FRAME_MS = 42;
const HOLD_MS = 4200;

type TextSetter = Dispatch<SetStateAction<string>>;

/** Builds one staggered split-flap frame that progressively resolves toward the target text. */
function buildFlapFrame(target: string, progress: number): string {
  const nonSpaceLength = Math.max(1, target.replaceAll(" ", "").length);
  let visibleIndex = 0;

  return [...target]
    .map((character) => {
      if (character === " ") {
        return " ";
      }

      visibleIndex += 1;
      const resolutionPoint = visibleIndex / nonSpaceLength;
      if (progress >= resolutionPoint) {
        return character;
      }

      return FLAP_CHARACTERS[
        Math.floor(Math.random() * FLAP_CHARACTERS.length)
      ];
    })
    .join("");
}

/** Schedules a complete split-flap resolution and returns the time at which the target is fully readable. */
function scheduleFlapAnimation(
  target: string,
  setText: TextSetter,
  startDelay: number,
  timers: number[],
): number {
  const frames = Math.max(16, target.replaceAll(" ", "").length);

  for (let frame = 0; frame <= frames; frame += 1) {
    const timer = window.setTimeout(
      () => {
        const progress = frame / frames;
        setText(progress >= 1 ? target : buildFlapFrame(target, progress));
      },
      startDelay + frame * FRAME_MS,
    );
    timers.push(timer);
  }

  return startDelay + frames * FRAME_MS;
}

/** Pads one board row to the fixed physical column count without animating unused cells. */
function boardCells(text: string): string[] {
  return [...text.padEnd(BOARD_COLUMNS, " ").slice(0, BOARD_COLUMNS)];
}

/** Renders one fixed split-flap row where every physical character position remains visible. */
function FlapLine({
  text,
  accent = false,
}: {
  text: string;
  accent?: boolean;
}) {
  const boardStyle = { "--board-columns": BOARD_COLUMNS } as CSSProperties;

  return (
    <div
      className={`flap-line${accent ? " flap-line-accent" : ""}`}
      style={boardStyle}
      aria-hidden="true"
    >
      {boardCells(text).map((character, index) => (
        <span
          className={`flap-character${character === " " ? " flap-character-blank" : ""}`}
          key={index}
        >
          {character === " " ? "\u00a0" : character}
        </span>
      ))}
    </div>
  );
}

/** Renders the hero identity board, resolving the introduction once and continuously rotating the approved descriptors. */
export default function SplitFlapHero() {
  const [introText, setIntroText] = useState("");
  const [descriptorText, setDescriptorText] = useState("");

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) {
      setIntroText(INTRO_TEXT);
      setDescriptorText(DESCRIPTORS[0]);
      return undefined;
    }

    const timers: number[] = [];
    const introFinishedAt = scheduleFlapAnimation(
      INTRO_TEXT,
      setIntroText,
      120,
      timers,
    );
    const descriptorFinishedAt = scheduleFlapAnimation(
      DESCRIPTORS[0],
      setDescriptorText,
      introFinishedAt + 180,
      timers,
    );

    let descriptorIndex = 0;
    const rotationStart = window.setTimeout(() => {
      descriptorIndex = 1;
      scheduleFlapAnimation(
        DESCRIPTORS[descriptorIndex],
        setDescriptorText,
        0,
        timers,
      );

      const rotation = window.setInterval(() => {
        descriptorIndex = (descriptorIndex + 1) % DESCRIPTORS.length;
        scheduleFlapAnimation(
          DESCRIPTORS[descriptorIndex],
          setDescriptorText,
          0,
          timers,
        );
      }, HOLD_MS);
      timers.push(rotation);
    }, descriptorFinishedAt + HOLD_MS);
    timers.push(rotationStart);

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const resolvedDescriptor =
    DESCRIPTORS.find((descriptor) => descriptor === descriptorText) ??
    DESCRIPTORS[0];

  return (
    <div className="split-flap-hero">
      <p className="sr-only" aria-live="polite">
        {INTRO_TEXT} {resolvedDescriptor}
      </p>
      <FlapLine text={introText} />
      <FlapLine text={descriptorText} accent />
    </div>
  );
}
